import { useMemo } from "react"
import {
  Avatar,
  Box,
  Button,
  Typography,
  useTheme,
  Card,
  ButtonProps,
  CardProps,
  Stack,
} from "@mui/material"
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined"
import size from "lodash/size"
import { getIcon, IConfigIcon } from "utils/icon"
import { useTranslation } from "providers/i18n"
import { useMobileUrl } from "hooks/useMobileUrl"
import { useImageModal } from "hooks/imageModal/useImageModal"
import { FeatureLabel } from "components/domains/features"
import { CloseIconButton } from "components/Button/CloseIconButton"
import { QRCode } from "components/QRCode"
import {
  StoreIcon,
  BulletedListIcon,
  ClockIcon,
  CloseIcon,
  LocationIcon,
} from "components/icons"
import { Image, ImageCarouselModal } from "components/Image"
import { Carousel, CarouselItem } from "components/Carousel"
import { GetFeatureDetailResult } from "utils/feature"
import { ComingSoonChip, NewShopChip, VatRefundChip } from "../../chips"

export type FeatureCardData = GetFeatureDetailResult & { [k: string]: any }

export interface IStandardFeatureCardProps {
  open?: boolean
  loading?: boolean
  isNavigatable?: boolean
  isNewShop?: boolean
  isComingSoon?: boolean
  isVatRefund?: boolean
  data: FeatureCardData
  directionButtonProps?: ButtonProps
  promotionButtonProps?: ButtonProps
  reservationButtonProps?: ButtonProps
  recommendedMenuButtonProps?: ButtonProps
  categoryIconProp?: IConfigIcon
  childrenProps?: CardProps
  onClose: () => void
  onNavigate?: () => void
  onClickPromotionButton?: () => void
  onClickReservationButton?: () => void
  onClickRecommendedMenuButton?: () => void
  onClickMultipleFloor?: (id: string) => void
}

export const FeatureCard: React.FC<IStandardFeatureCardProps> = ({
  open = false,
  directionButtonProps = {},
  promotionButtonProps = {},
  recommendedMenuButtonProps = {},
  reservationButtonProps = {},
  loading = true,
  data = {},
  categoryIconProp = {},
  childrenProps = {},
  isNavigatable = true,
  isNewShop = false,
  isComingSoon = false,
  isVatRefund = false,
  onClose,
  onNavigate = () => {},
  onClickPromotionButton = () => {},
  onClickReservationButton = () => {},
  onClickRecommendedMenuButton = () => {},
  onClickMultipleFloor = () => {},
  ...props
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { mobileUrl } = useMobileUrl()
  const {
    logo,
    category,
    name,
    hours,
    startDate,
    gallery,
    canReserve,
    description,
    promotions,
    venueLogo,
    products,
    allLevels,
  } = data as FeatureCardData
  const hasProducts = size(products) > 0
  const galleryImageLength = size(gallery)
  const galleryUrls = useMemo(
    () => (size(gallery) > 0 ? gallery?.map((v) => v?.url) : []),
    [gallery]
  )
  const { handleImage, openModal, activeImage, handleClose } = useImageModal()
  const opening = startDate?.locale("en").format("MMM/YYYY") || "-"

  const {
    root,
    primarySection,
    secondarySection,
    header,
    content,
    avatar,
    labelContainer,
    label,
    actionContainer,
    actionButton,
    listContainer,
    qrCodeContainer,
    qrCode,
    qrcodePrimaryText,
    qrcodeSecondaryText,
    closeButton,
    carousel,
    categoryLabel,
    operationTimeLabel,
    openingLabel,
    contentWrapper,
    imageSection,
    galleryImage,
    listItem,
    headerImage,
    statusContainer,
    newShopChip,
    comingSoonChip,
    vatRefundContainer,
    vatRefundChip,
    galleryCarouselModal,
  } = theme.components[`FeatureCard`]?.styleOverrides || {}

  const { galleryCarouselOption } =
    theme.components[`FeatureCard`]?.defaultProps || {}

  const closeIcon = useMemo(
    () => getIcon(theme?.assets?.closeIcon) || <CloseIcon />,
    [theme]
  )

  const locationIcon = useMemo(
    () => getIcon(theme?.assets?.floorIcon) || <LocationIcon />,
    [theme]
  )

  const categoryIcon = useMemo(
    () => getIcon(theme?.assets?.listIcon) || <BulletedListIcon />,
    [theme]
  )

  const clockIcon = useMemo(
    () => getIcon(theme?.assets?.clockIcon) || <ClockIcon />,
    [theme]
  )

  const calendarIcon = useMemo(
    () => getIcon(theme?.assets?.calendarIcon) || <CalendarTodayOutlinedIcon />,
    [theme]
  )

  const hasGallery = size(gallery) > 0

  const allLevelDisplay = useMemo(
    () => allLevels?.join(", ") || "-",
    [allLevels]
  )

  const direction = theme.direction
  return (
    <>
      <Card sx={root} {...childrenProps}>
        <CloseIconButton
          sx={closeButton}
          icon={closeIcon}
          onClick={onClose}
          size="large"
        />
        <Box
          className={hasGallery ? "content-with-gallery" : ""}
          sx={contentWrapper}
        >
          <Box className="primarySection" sx={primarySection}>
            <Box sx={header}>
              <Avatar sx={avatar} src={logo}>
                {getIcon(categoryIconProp, null, {
                  imageIconProps: {
                    solidColorFilterId: "placeDetail_avatar_filter",
                  },
                }) || <StoreIcon />}
              </Avatar>
              <Box sx={labelContainer}>
                <Box sx={statusContainer}>
                  {isNewShop && <NewShopChip sx={newShopChip} />}
                  {isComingSoon && (
                    <ComingSoonChip
                      sx={comingSoonChip}
                      label={t("Coming soon")}
                    />
                  )}
                </Box>
                <Typography sx={label} gutterBottom variant="h5">
                  {t(name)}
                </Typography>
              </Box>
            </Box>
            {isVatRefund && (
              <Box sx={vatRefundContainer}>
                <VatRefundChip sx={vatRefundChip} />
              </Box>
            )}
            {description && (
              <Box sx={content}>
                <Typography variant="body2" color="text.secondary">
                  {t(description)}
                </Typography>
              </Box>
            )}
            {mobileUrl && (
              <>
                <Box sx={qrCodeContainer}>
                  <QRCode sx={qrCode} url={mobileUrl} />
                  <Stack>
                    <Typography
                      variant="subtitle2"
                      component="p"
                      sx={qrcodePrimaryText}
                    >
                      {t("Scan Here")}
                    </Typography>
                    <Typography
                      variant="caption"
                      component="p"
                      sx={qrcodeSecondaryText}
                    >
                      {t("For Mobile")}
                    </Typography>
                  </Stack>
                </Box>
              </>
            )}
          </Box>
          <Box sx={secondarySection}>
            <Image src={venueLogo} sx={headerImage} />
            <Box sx={listContainer}>
              <Box sx={listItem}>
                {locationIcon}
                <FeatureLabel label={t("Floor")} value={t(allLevelDisplay)} />
              </Box>
              <Box sx={listItem}>
                {categoryIcon}
                <FeatureLabel
                  label={t("Category")}
                  value={t(category)}
                  sx={categoryLabel}
                />
              </Box>
              {!isComingSoon && (
                <Box sx={listItem}>
                  {clockIcon}
                  <FeatureLabel
                    label={t("Operation Time")}
                    value={hours || "-"}
                    sx={operationTimeLabel}
                  />
                </Box>
              )}
              {isComingSoon && (
                <Box sx={listItem} className="is-coming-soon">
                  {calendarIcon}
                  <FeatureLabel
                    label={t("Opening")}
                    value={opening}
                    sx={openingLabel}
                  />
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={imageSection}>
            {galleryImageLength > 0 && (
              <Box>
                <Carousel
                  options={{
                    rewind: false,
                    trimSpace: false,
                    pagination: true,
                    perPage: 1,
                    focus: "center",
                    gap: 0,
                    width: 360,
                    height: 242,
                    padding: 0,
                    margin: 0,
                    autoplay: true,
                    arrows: galleryImageLength > 1,
                    direction: direction,
                    ...galleryCarouselOption,
                  }}
                  sx={carousel}
                >
                  {gallery?.map((image, idx) => (
                    <CarouselItem key={`${image.name}-${idx}`}>
                      <Image
                        onClick={() => handleImage(image.url)}
                        src={image.url}
                        alt={`${image.name}-${idx}`}
                        sx={galleryImage}
                      />
                    </CarouselItem>
                  ))}
                </Carousel>
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={actionContainer}>
          {promotions.length > 0 && !isComingSoon && (
            <Button
              variant="outlined"
              onClick={onClickPromotionButton}
              sx={actionButton}
              {...promotionButtonProps}
            >
              {t("Promotion")}
            </Button>
          )}
          {hasProducts && !isComingSoon && (
            <Button
              variant="outlined"
              onClick={onClickRecommendedMenuButton}
              sx={actionButton}
              {...recommendedMenuButtonProps}
            >
              {t("Recommended Menu")}
            </Button>
          )}
          {canReserve && !isComingSoon && (
            <Button
              variant="outlined"
              onClick={onClickReservationButton}
              sx={actionButton}
              {...reservationButtonProps}
            >
              {t("Reservation")}
            </Button>
          )}
          <Button
            id="get-direction-button"
            data-testid="get-direction-button"
            variant="contained"
            onClick={onNavigate}
            className="highlight"
            {...directionButtonProps}
            sx={{
              ...actionButton,
              visibility: isNavigatable ? "visible" : "hidden",
            }}
            disabled={isComingSoon}
          >
            {isComingSoon ? t("Coming Soon") : t("Get direction")}
          </Button>
        </Box>
      </Card>
      <ImageCarouselModal
        className="FeatureCard-galleryCarouselModal"
        sx={galleryCarouselModal}
        activeSrc={activeImage}
        open={openModal}
        imageSrcs={galleryUrls}
        onClose={handleClose}
        carouselOptions={{
          rewind: false,
        }}
      />
    </>
  )
}
